import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, of, tap} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from "@env/environment";
import {Landlord, Location, Offering} from "@app/security/admin-context.service";

@Injectable({providedIn: 'root'})
export class AdminContextSelectorService {
  constructor(private http: HttpClient) {
  }

  // Landlords come from the admin (offering-independent) list so a freshly
  // created landlord with no locations/offerings still appears; offerings are
  // merged in per landlord from the offering view.
  private landlordsUrl = environment.apiLandlordHost + '/admin/landlords';
  private offeringsUrl = environment.apiLandlordHost + '/api/landlord/offering';

  private readonly _LANDLORDS_CONTEXT_CACHE_KEY = 'landlords-context-cache';

  getLandlords(fetchFromServer = false): Observable<Landlord[]> {
    if (!fetchFromServer) {
      const cachedLandlords = sessionStorage.getItem(this._LANDLORDS_CONTEXT_CACHE_KEY);
      if (cachedLandlords) {
        return of(JSON.parse(cachedLandlords));
      }
    }

    return forkJoin({
      landlords: this.http.get<any[]>(this.landlordsUrl),
      offerings: this.http.get<any[]>(this.offeringsUrl),
    }).pipe(
      map(({landlords, offerings}) => this.transformToHierarchy(landlords, offerings)),
      tap(data => sessionStorage.setItem(this._LANDLORDS_CONTEXT_CACHE_KEY, JSON.stringify(data))),
      catchError(error => {
        console.error('Error fetching landlord data', error);
        return of([]);
      })
    );
  }

  private transformToHierarchy(landlordRows: any[], offeringRows: any[]): Landlord[] {
    const landlordsMap = new Map<string, Landlord>();
    const locationsMap = new Map<string, Location>();

    // Seed every active landlord and its active locations first, so both an
    // offering-less landlord AND an offering-less location still appear.
    landlordRows.forEach(row => {
      const locations: Location[] = (row.locations ?? []).map((loc: any) => {
        const location: Location = {uuid: loc.uuid, name: loc.name, offerings: []};
        locationsMap.set(loc.uuid, location);
        return location;
      });
      landlordsMap.set(row.uuid, {uuid: row.uuid, name: row.companyName, locations});
    });

    const offeringsMap = new Map<string, Offering>();

    offeringRows.forEach(item => {
      if (offeringsMap.has(item.offeringUuid)) {
        return;
      }
      const offering: Offering = {uuid: item.offeringUuid, name: item.offeringName};
      offeringsMap.set(item.offeringUuid, offering);

      let landlord = landlordsMap.get(item.landlordUuid);
      if (!landlord) {
        // Offering references a landlord absent from the active list — fall back
        // to the offering row so nothing that exists today disappears.
        landlord = {uuid: item.landlordUuid, name: item.companyName, locations: []};
        landlordsMap.set(item.landlordUuid, landlord);
      }

      let location = locationsMap.get(item.locationUuid);
      if (!location) {
        // Location absent from the active list — fall back to the offering row.
        location = {uuid: item.locationUuid, name: item.locationName, offerings: []};
        locationsMap.set(item.locationUuid, location);
        landlord.locations.push(location);
      }
      if (!location.offerings.find(o => o.uuid === offering.uuid)) {
        location.offerings.push(offering);
      }
    });

    return Array.from(landlordsMap.values());
  }
}
